/**
 * Operation types that require different authentication strategies
 * This type defines the classification of operations based on their security requirements.
 * Critical operations require EIP-712 signatures, while low-security operations can use session tokens.
 */

export type OperationType = string;

// Low-security operations that can use session tokens
export const OperationTypeGetBalance: OperationType = "sys::balance";
export const OperationTypeGetPositions: OperationType = "sys::positions";
export const OperationTypeGetOrderHistory: OperationType = "sys::order_history";
export const OperationTypeGetTradeHistory: OperationType = "sys::trade_history";
export const OperationTypeGetMarketData: OperationType = "sys::market_data";
export const OperationTypeGetAddressInfo: OperationType = "sys::user_info";
export const OperationTypeGetSessionInfo: OperationType = "sys::session_info";
export const OperationTypeGetAuthMethods: OperationType = "sys::auth_methods";
export const OperationTypeGetHealthStatus: OperationType = "sys::health_status";
export const OperationTypeGetSystemInfo: OperationType = "sys::system_info";
export const OperationTypeGetNotifications: OperationType = "sys::notifications";
export const OperationTypeGetPreferences: OperationType = "sys::preferences";
export const OperationTypeGetStatistics: OperationType = "sys::statistics";
export const OperationTypeGetReports: OperationType = "sys::reports";
export const OperationTypeGetAnalytics: OperationType = "sys::analytics";
export const OperationTypeGetDocumentation: OperationType = "sys::documentation";
export const OperationTypeGetHelp: OperationType = "sys::help";
export const OperationTypeGetSupport: OperationType = "sys::support";
export const OperationTypeGetFeedback: OperationType = "sys::feedback";

// Authentication operations
export const OperationTypeOnboard: OperationType = "sys::onboard";
export const OperationTypeConnect: OperationType = "sys::connect";

// Legacy operation types for backward compatibility
// Critical operations requiring EIP-712 signatures
// DEX-specific write operations
export const OpChangeMarginRatio: OperationType = "clob::change_margin_ratio";
// @deprecated - No longer used
export const OpChangeBucket: OperationType = "clob::change_bucket";
export const OpSubmitOrder: OperationType = "clob::submit_order";
export const OpCancelOrder: OperationType = "clob::cancel_order";
export const OpMarketOrder: OperationType = "clob::market_order";
export const OpLiquidation: OperationType = "clob::liquidate_position";
export const OpClosePosition: OperationType = "bucket::close_position";
export const OpMatchOrder: OperationType = "clob::match_order";
export const OpModifyOrder: OperationType = "clob::modify_order";
export const OpChangeLeverage: OperationType = "clob::change_leverage";
export const OpInternTransfer: OperationType = "clob::inter_transfer";
export const OpCreateMarket: OperationType = "clob::create_market";
export const OpCreateMarginBucket: OperationType = "clob::create_margin_bucket";
export const OpUpdateBucketMargin: OperationType = "clob::update_bucket_margin";
// @deprecated - Replaced by v2 bridge
export const OpWithdraw: OperationType = "clob::withdraw";
// @deprecated - Replaced by v2 bridge
export const OpDeposit: OperationType = "clob::deposit";
export const OpSwap: OperationType = "clob::swap";

// Bank operations (Critical - Require EIP-712 Signatures)
// These operations handle asset management, bucket operations, and transfers
export const OpBankMint: OperationType = "bank::mint";
export const OpBankExtractToBucket: OperationType = "bank::extract_to_bucket";
export const OpBankWithdrawFromBucket: OperationType = "bank::withdraw_from_bucket";
export const OpBankBucketTransfer: OperationType = "bank::bucket_transfer";
export const OpBankTransfer: OperationType = "bank::transfer";

// Legacy aliases for backward compatibility
// @deprecated - Use OpBankExtractToBucket instead
export const OpExtractToBucket: OperationType = "bank::extract_to_bucket";
// @deprecated - Use OpBankWithdrawFromBucket instead
export const OpWithdrawFromBucket: OperationType = "bank::withdraw_from_bucket";
// @deprecated - Use OpBankBucketTransfer instead
export const OpBucketTransfer: OperationType = "bank::bucket_transfer";

// Hyperlane cross-chain operations
export const OpMailboxIncoming: OperationType = "hyperlane::inbox";
export const OpMailboxOutgoing: OperationType = "hyperlane::outbox";
export const OpISMVerificationPartial: OperationType = "hyperlane::ism_step";
export const OpISMVerificationFinal: OperationType = "hyperlane::finale";
export const OpCrossChainBurn: OperationType = "hyperlane::burn";
export const OpCrossChainFreeze: OperationType = "hyperlane::freeze";
export const OpCrossChainUnFreeze: OperationType = "hyperlane::unfreeze";
export const OpHyperlaneOrderExecution: OperationType = "hyperlane::order_execution";
export const OpHyperlaneGovernance: OperationType = "hyperlane::governance";

// Governance operations
export const OpPostProposal: OperationType = "gov::post_proposal";
export const OpModifyProposal: OperationType = "gov::patch_proposal";
export const OpVoteProposal: OperationType = "gov::vote";
export const OpExecuteProposalParam: OperationType = "gov::exe_proposal_param";
export const OpExecuteProposalModule: OperationType = "gov::exe_proposal_module";
export const OpExecuteProposalSpot: OperationType = "gov::proposal_spot";
export const OpExecuteProposalPerp: OperationType = "gov::proposal_prelaunch_perp";
export const OpExecuteProposalOptionsPerp: OperationType = "gov::proposal_options_perp";
export const OpExecuteProposalDelisting: OperationType = "gov::delist";

// Proposal lifecycle management operations
export const OpWithdrawProposal: OperationType = "gov::withdraw_proposal";
export const OpCancelProposal: OperationType = "gov::cancel_proposal";
export const OpExtendProposal: OperationType = "gov::extend_proposal";
export const OpQueryProposal: OperationType = "gov::query_proposal";

// Emergency governance operations
export const OpEmergencyPause: OperationType = "gov::emergency_pause";
export const OpEmergencyResume: OperationType = "gov::emergency_resume";
export const OpEmergencyUpgrade: OperationType = "gov::emergency_upgrade";

// Governance configuration operations
export const OpUpdateGovernanceParams: OperationType = "gov::update_params";
export const OpUpdateQuorum: OperationType = "gov::update_quorum";
export const OpUpdateVotingPeriod: OperationType = "gov::update_voting_period";

// Delegation and voting operations
export const OpDelegateVotingPower: OperationType = "gov::delegate_voting";
export const OpUndelegateVotingPower: OperationType = "gov::undelegate_voting";
export const OpChangeDelegation: OperationType = "gov::change_delegation";

// Treasury and funding operations
export const OpTreasuryTransfer: OperationType = "gov::treasury_transfer";
export const OpTreasuryAllocation: OperationType = "gov::treasury_allocation";
export const OpFundingProposal: OperationType = "gov::funding_proposal";

// Governance metadata operations
export const OpUpdateGovernanceMetadata: OperationType = "gov::update_metadata";
export const OpSetGovernanceDescription: OperationType = "gov::set_description";

// Validator management operations
export const OpAddValidator: OperationType = "gov::add_validator";
export const OpRemoveValidator: OperationType = "gov::remove_validator";
export const OpUpdateValidatorParams: OperationType = "gov::update_validator_params";
export const OpSetValidatorCommission: OperationType = "gov::set_validator_commission";

// Validator enforcement operations
export const OpSlashValidator: OperationType = "gov::slash_validator";
export const OpPauseValidator: OperationType = "gov::pause_validator";
export const OpResumeValidator: OperationType = "gov::resume_validator";
export const OpJailValidator: OperationType = "gov::jail_validator";
export const OpUnjailValidator: OperationType = "gov::unjail_validator";

// Validator metadata operations
export const OpSetValidatorDescription: OperationType = "gov::set_validator_description";
export const OpUpdateValidatorInfo: OperationType = "gov::update_validator_info";

// Validator economics operations
export const OpSetValidatorRewards: OperationType = "gov::set_validator_rewards";
export const OpDistributeValidatorRewards: OperationType = "gov::distribute_validator_rewards";
export const OpUpdateValidatorStake: OperationType = "gov::update_validator_stake";

// Token lifecycle operations
export const OpMintToken: OperationType = "token::mint";
export const OpBurnToken: OperationType = "token::burn";
export const OpPauseToken: OperationType = "token::pause";
export const OpUnpauseToken: OperationType = "token::unpause";
export const OpFreezeToken: OperationType = "token::freeze";
export const OpUnfreezeToken: OperationType = "token::unfreeze";
export const OpIssueToken: OperationType = "token::issue";
export const OpEndorseToken: OperationType = "token::endorse";

// Token transfer operations
export const OpTransferToken: OperationType = "token::transfer";
export const OpTransferFromToken: OperationType = "token::transfer_from";
export const OpBatchTransferToken: OperationType = "token::batch_transfer";

// Token approval operations
export const OpApproveToken: OperationType = "token::approve";
export const OpRevokeApproval: OperationType = "token::revoke_approval";
export const OpSetApprovalForAll: OperationType = "token::set_approval_for_all";
export const OpRevokeApprovalForAll: OperationType = "token::revoke_approval_for_all";

// Token governance and control operations
export const OpUpdateTokenMetadata: OperationType = "token::update_metadata";
export const OpSetTokenURI: OperationType = "token::set_uri";
export const OpUpdateTokenURI: OperationType = "token::update_uri";
export const OpSetTokenRoyalty: OperationType = "token::set_royalty";
export const OpUpdateTokenRoyalty: OperationType = "token::update_royalty";

// Token access control operations
export const OpWhitelistToken: OperationType = "token::whitelist";
export const OpBlacklistToken: OperationType = "token::blacklist";
export const OpRemoveFromWhitelist: OperationType = "token::remove_whitelist";
export const OpRemoveFromBlacklist: OperationType = "token::remove_blacklist";
export const OpSetTokenCap: OperationType = "token::set_cap";
export const OpUpdateTokenCap: OperationType = "token::update_cap";

// Token economics operations
export const OpSetTokenRewards: OperationType = "token::set_rewards";
export const OpDistributeTokenRewards: OperationType = "token::distribute_rewards";
export const OpClaimTokenRewards: OperationType = "token::claim_rewards";
export const OpStakeToken: OperationType = "token::stake";
export const OpUnstakeToken: OperationType = "token::unstake";
export const OpSetTokenInflation: OperationType = "token::set_inflation";
export const OpUpdateTokenInflation: OperationType = "token::update_inflation";

// Token security operations
export const OpSetTokenGuardian: OperationType = "token::set_guardian";
export const OpUpdateTokenGuardian: OperationType = "token::update_guardian";
export const OpRevokeTokenGuardian: OperationType = "token::revoke_guardian";
export const OpSetTokenCompliance: OperationType = "token::set_compliance";
export const OpUpdateTokenCompliance: OperationType = "token::update_compliance";
export const OpSetTokenKYC: OperationType = "token::set_kyc";
export const OpUpdateTokenKYC: OperationType = "token::update_kyc";

// Staking operations
export const OpIssuePool: OperationType = "stake::issue_pool";
export const OpStake: OperationType = "stake::stake";
export const OpStakeCommit: OperationType = "stake::commit";
export const OpStakeConfirm: OperationType = "stake::invest_confirm";
export const OpRestakeCommit: OperationType = "stake::restake_commit";
export const OpRestakeConfirm: OperationType = "stake::restake_confirm";
export const OpInterestRateAdjustment: OperationType = "stake::update_ir";
export const OpStakeWithdraw: OperationType = "stake::withdraw_token";
export const OpCollateral: OperationType = "stake::collateral_conversion";
export const OpBorrow: OperationType = "stake::borrow";
export const OpRepay: OperationType = "stake::repay";
export const OpLiquidate: OperationType = "stake::liquidate";
export const OpClaimRewards: OperationType = "stake::claim_rewards";
export const OpRedeemCollateral: OperationType = "stake::redeem_collateral";
export const OpDelegate: OperationType = "stake::delegate";
export const OpUndelegate: OperationType = "stake::undelegate";
export const OpRedelegate: OperationType = "stake::redelegate";
export const OpUnstakeRequest: OperationType = "stake::unstake_request";

// Hyper-CLAMM read operations (Session Token Authentication)
export const OpGetPoolInfo: OperationType = "hyperclamm::get_pool_info";
export const OpGetPosition: OperationType = "hyperclamm::get_position";
export const OpGetSwapQuote: OperationType = "hyperclamm::get_swap_quote";
export const OpGetLiquidityDepth: OperationType = "hyperclamm::get_liquidity_depth";
export const OpGetHookFlags: OperationType = "hyperclamm::get_hook_flags";
export const OpGetInvariantBounds: OperationType = "hyperclamm::get_invariant_bounds";

// Hyper-CLAMM write operations (Critical - Require EIP-712 Signatures)
export const OpCreatePool: OperationType = "hyperclamm::create_pool";
export const OpAddLiquidityBalanced: OperationType = "hyperclamm::add_liquidity_balanced";
export const OpAddLiquidityUnbalanced: OperationType = "hyperclamm::add_liquidity_unbalanced";
export const OpAddLiquidityCustom: OperationType = "hyperclamm::add_liquidity_custom";
export const OpRemoveLiquidityProportional: OperationType = "hyperclamm::remove_liquidity_proportional";
export const OpRemoveLiquidityUnbalanced: OperationType = "hyperclamm::remove_liquidity_unbalanced";
export const OpRemoveLiquiditySingleAsset: OperationType = "hyperclamm::remove_liquidity_single_asset";
export const OpSwapExactIn: OperationType = "hyperclamm::swap_exact_in";
export const OpSwapExactOut: OperationType = "hyperclamm::swap_exact_out";
export const OpCollectFees: OperationType = "hyperclamm::collect_fees";
export const OpUpdatePosition: OperationType = "hyperclamm::update_position";
export const OpInvokeHook: OperationType = "hyperclamm::invoke_hook";
export const OpBoostedAddRemove: OperationType = "hyperclamm::boosted_add_remove";
export const OpDonateFees: OperationType = "hyperclamm::donate_fees";
export const OpStakeLPShares: OperationType = "hyperclamm::stake_lp_shares";
export const OpUseLPCollateral: OperationType = "hyperclamm::use_lp_collateral";

// Testnet-specific operations
export const OpSetBalance: OperationType = "testnet::set_balance";
export const OpClaimToken: OperationType = "testnet::faucet";
export const OpFakeDeposit: OperationType = "testnet::fake_deposit";

// Session token operations (low security)
export const OpViewMetadata: OperationType = "view_metadata";
export const OpUpdateTheme: OperationType = "update_theme";
export const OpUpdateTimezone: OperationType = "update_timezone";
export const OpBindXAccount: OperationType = "bind_x_account";
export const OpFollowAddress: OperationType = "follow_user";
export const OpViewAssets: OperationType = "view_assets";
export const OpViewTrades: OperationType = "view_trades";
export const OpViewBalance: OperationType = "view_balance";
export const OpRevokeSession: OperationType = "revoke_session";
export const OpRead: OperationType = "read";

// Reputation and oracle operations
export const OpGetReputation: OperationType = "reput::get_reputation_score";
export const OpGetAccountRepDetail: OperationType = "reput::get_detail";
export const OpSubmitActivity: OperationType = "reput::submit_activity";
export const OpSlashReputation: OperationType = "reput::slash";
export const OpADLExecution: OperationType = "oracle::adl_execution";

// Multi-sign wallet deployment and management operations
export const OpDeployMultiSignWallet: OperationType = "multisig::deploy_wallet";
export const OpSetupWalletOwners: OperationType = "multisig::setup_owners";
export const OpUpdateWalletThreshold: OperationType = "multisig::update_threshold";
export const OpAddWalletOwner: OperationType = "multisig::add_owner";
export const OpRemoveWalletOwner: OperationType = "multisig::remove_owner";
export const OpReplaceWalletOwner: OperationType = "multisig::replace_owner";
export const OpSwapWalletOwner: OperationType = "multisig::swap_owner";

// Multi-sign wallet transaction operations
export const OpSubmitMultiSignTx: OperationType = "multisig::submit_tx";
export const OpApproveMultiSignTx: OperationType = "multisig::approve_tx";
export const OpRejectMultiSignTx: OperationType = "multisig::reject_tx";
export const OpExecuteMultiSignTx: OperationType = "multisig::execute_tx";
export const OpCancelMultiSignTx: OperationType = "multisig::cancel_tx";
export const OpRevokeMultiSignApproval: OperationType = "multisig::revoke_approval";

// Multi-sign wallet module and guard operations
export const OpEnableWalletModule: OperationType = "multisig::enable_module";
export const OpDisableWalletModule: OperationType = "multisig::disable_module";
export const OpSetWalletFallbackHandler: OperationType = "multisig::set_fallback";
export const OpUpdateWalletFallback: OperationType = "multisig::update_fallback";
export const OpSetWalletGuard: OperationType = "multisig::set_guard";
export const OpUpdateWalletGuard: OperationType = "multisig::update_guard";

// Multi-sign wallet recovery and emergency operations
export const OpInitiateWalletRecovery: OperationType = "multisig::initiate_recovery";
export const OpConfirmWalletRecovery: OperationType = "multisig::confirm_recovery";
export const OpCancelWalletRecovery: OperationType = "multisig::cancel_recovery";
export const OpEmergencyWalletPause: OperationType = "multisig::emergency_pause";
export const OpEmergencyWalletResume: OperationType = "multisig::emergency_resume";

// Multi-sign wallet query and status operations
export const OpGetWalletInfo: OperationType = "multisig::get_wallet_info";
export const OpGetWalletOwners: OperationType = "multisig::get_owners";
export const OpGetWalletThreshold: OperationType = "multisig::get_threshold";
export const OpGetWalletModules: OperationType = "multisig::get_modules";
export const OpGetWalletTxHistory: OperationType = "multisig::get_tx_history";
export const OpGetPendingWalletTxs: OperationType = "multisig::get_pending_txs";
export const OpGetWalletNonce: OperationType = "multisig::get_nonce";

// Multi-sign wallet factory operations
export const OpDeployWalletFactory: OperationType = "multisig::deploy_factory";
export const OpUpdateFactoryMasterCopy: OperationType = "multisig::update_master";
export const OpSetFactoryProxyCode: OperationType = "multisig::set_proxy_code";
export const OpGetFactoryInfo: OperationType = "multisig::get_factory_info";

// Cross-signature type operations for multi-sign wallets
export const OpMultiSignWithECDSA: OperationType = "multisig::sign_ecdsa";
export const OpMultiSignWithSchnorr: OperationType = "multisig::sign_schnorr";
export const OpMultiSignAggregate: OperationType = "multisig::sign_aggregate";
export const OpVerifyMultiSignCrossType: OperationType = "multisig::verify_cross_type";

// Agent delegation operations
export const OpApproveAgent: OperationType = "agent::approve";
export const OpRevokeAgent: OperationType = "agent::revoke";

// ERC-721 Core Write Operations (Critical - Require EIP-712 Signatures)
export const OpNFTMint: OperationType = "nft::mint";
export const OpNFTTransfer: OperationType = "nft::transfer";
export const OpNFTSafeTransfer: OperationType = "nft::safe_transfer";
export const OpNFTApprove: OperationType = "nft::approve";
export const OpNFTSetApprovalForAll: OperationType = "nft::set_approval_for_all";
export const OpNFTBurn: OperationType = "nft::burn";

// ERC-1155 Multi-Token Write Operations (Critical - Require EIP-712 Signatures)
export const OpNFT1155Mint: OperationType = "nft1155::mint";
export const OpNFT1155MintBatch: OperationType = "nft1155::mint_batch";
export const OpNFT1155Transfer: OperationType = "nft1155::transfer";
export const OpNFT1155TransferBatch: OperationType = "nft1155::transfer_batch";
export const OpNFT1155Burn: OperationType = "nft1155::burn";
export const OpNFT1155BurnBatch: OperationType = "nft1155::burn_batch";

// ERC-2981 Royalty Write Operations (Critical - Require EIP-712 Signatures)
export const OpNFTSetRoyalty: OperationType = "nft::set_royalty";
export const OpNFTUpdateRoyalty: OperationType = "nft::update_royalty";
export const OpNFTClaimRoyalty: OperationType = "nft::claim_royalty";

// ERC-3525 Semi-Fungible Token Write Operations (Critical - Require EIP-712 Signatures)
export const OpSFTMint: OperationType = "sft::mint";
export const OpSFTTransferValue: OperationType = "sft::transfer_value";
export const OpSFTApproveValue: OperationType = "sft::approve_value";
export const OpSFTChangeSlot: OperationType = "sft::change_slot";

// ERC-4907 Rental NFT Write Operations (Critical - Require EIP-712 Signatures)
export const OpNFTSetAddress: OperationType = "nft::set_address";
export const OpNFTRevokeAddress: OperationType = "nft::revoke_address";
export const OpNFTExtendRental: OperationType = "nft::extend_rental";

// ERC-998 Composable NFT Write Operations (Critical - Require EIP-712 Signatures)
export const OpNFTTransferChild: OperationType = "nft::transfer_child";
export const OpNFTSafeTransferChild: OperationType = "nft::safe_transfer_child";
export const OpNFTGetChild: OperationType = "nft::get_child";

// Advanced NFT Write Operations (Critical - Require EIP-712 Signatures)
export const OpNFTLock: OperationType = "nft::lock";
export const OpNFTUnlock: OperationType = "nft::unlock";
export const OpNFTDelegate: OperationType = "nft::delegate";
export const OpNFTUndelegate: OperationType = "nft::undelegate";
export const OpNFTFractionalize: OperationType = "nft::fractionalize";
export const OpNFTSetMetadata: OperationType = "nft::set_metadata";
export const OpNFTSetTokenURI: OperationType = "nft::set_token_uri";

// ERC-721 Read Operations (Session Token Authentication)
export const OpNFTGetBalance: OperationType = "nft::get_balance";
export const OpNFTGetOwner: OperationType = "nft::get_owner";
export const OpNFTGetApproved: OperationType = "nft::get_approved";
export const OpNFTIsApprovedForAll: OperationType = "nft::is_approved_for_all";
export const OpNFTGetTokenURI: OperationType = "nft::get_token_uri";
export const OpNFTGetName: OperationType = "nft::get_name";
export const OpNFTGetmoniker: OperationType = "nft::get_moniker";
export const OpNFTGetTotalSupply: OperationType = "nft::get_total_supply";
export const OpNFTGetTokenByIndex: OperationType = "nft::get_token_by_index";
export const OpNFTGetTokenOfOwnerByIndex: OperationType = "nft::get_token_of_owner_by_index";

// ERC-1155 Read Operations (Session Token Authentication)
export const OpNFT1155GetBalance: OperationType = "nft1155::get_balance";
export const OpNFT1155GetBalanceBatch: OperationType = "nft1155::get_balance_batch";
export const OpNFT1155GetURI: OperationType = "nft1155::get_uri";

// ERC-2981 Royalty Read Operations (Session Token Authentication)
export const OpNFTGetRoyaltyInfo: OperationType = "nft::get_royalty_info";
export const OpNFTGetRoyaltyBalance: OperationType = "nft::get_royalty_balance";

// ERC-3525 Semi-Fungible Read Operations (Session Token Authentication)
export const OpSFTGetValue: OperationType = "sft::get_value";
export const OpSFTGetSlot: OperationType = "sft::get_slot";
export const OpSFTGetValueDecimals: OperationType = "sft::get_value_decimals";
export const OpSFTGetAllowance: OperationType = "sft::get_allowance";

// ERC-4907 Rental Read Operations (Session Token Authentication)
export const OpNFTGetAddress: OperationType = "nft::get_user";
export const OpNFTGetAddressExpires: OperationType = "nft::get_user_expires";

// ERC-998 Composable Read Operations (Session Token Authentication)
export const OpNFTGetRootOwner: OperationType = "nft::get_root_owner";
export const OpNFTGetChildOwner: OperationType = "nft::get_child_owner";
export const OpNFTGetChildBalance: OperationType = "nft::get_child_balance";

// Advanced NFT Read Operations (Session Token Authentication)
export const OpNFTGetLockStatus: OperationType = "nft::get_lock_status";
export const OpNFTGetDelegation: OperationType = "nft::get_delegation";
export const OpNFTGetFractionalInfo: OperationType = "nft::get_fractional_info";
export const OpNFTGetMetadata: OperationType = "nft::get_metadata";
export const OpNFTGetCollectionInfo: OperationType = "nft::get_collection_info";
export const OpNFTGetOwnershipHistory: OperationType = "nft::get_ownership_history";
export const OpNFTGetTransactionHistory: OperationType = "nft::get_transaction_history";

// x402 off-chain payment settlement gateway
export const OpX402Settlement: OperationType = "x402::settle_micropayment";
